import type { EstadoOt } from '@sime/shared';
import { STATE_LABELS, STATE_COLORS, ALL_STATES } from '@sime/shared';
import { Eye, EyeOff, RotateCcw } from 'lucide-react';
import { cn } from '@/lib/utils';

interface StatusFilterProps {
  activeFilters: Set<string>;
  byStatus: Record<string, number>;
  onToggle: (estado: EstadoOt) => void;
  onReset: () => void;
}

export function StatusFilter({ activeFilters, byStatus, onToggle, onReset }: StatusFilterProps) {
  const allActive = ALL_STATES.every((s) => activeFilters.has(s));

  return (
    <div className="flex flex-wrap items-center gap-2">
      {ALL_STATES.map((estado) => {
        const count = byStatus[estado] ?? 0;
        const isActive = activeFilters.has(estado);
        const colors = STATE_COLORS[estado];

        return (
          <button
            key={estado}
            type="button"
            onClick={() => { onToggle(estado); }}
            aria-pressed={isActive}
            aria-label={`${isActive ? 'Ocultar' : 'Mostrar'} ${STATE_LABELS[estado]} (${count})`}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-medium transition-all duration-150',
              'shadow-neu-sm',
              'hover:shadow-neu-xs',
              !isActive && 'opacity-40 grayscale',
            )}
            style={
              isActive
                ? { backgroundColor: colors.bg, color: colors.text }
                : { backgroundColor: 'var(--sime-surface)', color: 'var(--sime-text-secondary)' }
            }
          >
            {isActive
              ? <Eye className="h-3 w-3" aria-hidden="true" />
              : <EyeOff className="h-3 w-3" aria-hidden="true" />}
            {STATE_LABELS[estado]}
            {count > 0 ? (
              <span className={cn(
                'rounded-full px-1.5 py-0.5 text-[10px] leading-none',
                isActive ? 'bg-white/25' : 'bg-sime-surface-raised',
              )}>
                {count}
              </span>
            ) : null}
          </button>
        );
      })}

      {/* Reset button */}
      {!allActive ? (
        <button
          type="button"
          onClick={onReset}
          aria-label="Ver todos los estados"
          className={cn(
            'inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-medium',
            'text-sime-text-secondary transition-all duration-150',
            'shadow-neu-sm',
            'hover:text-sime-text-primary',
          )}
        >
          <RotateCcw className="h-3 w-3" aria-hidden="true" />
          Ver todos
        </button>
      ) : null}
    </div>
  );
}
